import "./NotificationCard.css";

import { NotificationRaisedByType } from "src/models";

import { KanaLogomark } from "../../../../assets";
import { renderWithLinks } from "../../../../utils";

export type NotificationCardVariant = "seed" | "hub";

export interface NotificationCardProps {
  text: string;
  date: string;
  raisedBy: NotificationRaisedByType;
  userInitials?: string;
  unread?: boolean;
  dismissed?: boolean;
  variant?: NotificationCardVariant;
  onDismiss?: () => void;
  onClick?: () => void;
}

export const NotificationCard = ({
  text,
  date,
  raisedBy,
  userInitials = "?",
  unread = false,
  dismissed = false,
  variant = "hub",
  onDismiss,
  onClick,
}: NotificationCardProps): JSX.Element => {
  const className = `NotificationCard ${variant}${dismissed ? " Dismissed" : ""}`;

  const content = (
    <>
      {!dismissed && onDismiss && (
        <button
          type="button"
          className="NotificationCardDismiss"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          aria-label="Dismiss notification"
        >
          ×
        </button>
      )}
      <div className="NotificationCardAvatar">
        {raisedBy === "System" ? (
          <div className="NotificationCardSystemIcon">
            <KanaLogomark />
          </div>
        ) : (
          <div className="NotificationCardUserInitials">{userInitials}</div>
        )}
      </div>
      <div className="NotificationCardContent">
        <p className="NotificationCardText">{renderWithLinks(text)}</p>
        <p className="NotificationCardDate">{date}</p>
      </div>
      {unread && !dismissed && <div className="NotificationCardUnreadDot" />}
    </>
  );

  if (onClick) {
    // Not a <button>: the card holds the dismiss button and the text's links, and interactive content
    // cannot nest inside a button. The role and key handling keep it reachable and operable as one.
    return (
      <div
        role="button"
        tabIndex={0}
        className={className}
        onClick={onClick}
        onKeyDown={(e) => {
          // Only the card's own key presses - Enter on the dismiss button or a link must not open it too.
          if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
          e.preventDefault();
          onClick();
        }}
      >
        {content}
      </div>
    );
  }

  return <div className={className}>{content}</div>;
};
